import {
  useState, useRef, useCallback, ChangeEvent, memo, Fragment, ReactElement,
} from 'react';
import classNames from 'classnames';
import Tooltip, { placementsList } from '@picsart/rc/old/Tooltip';
import IconChevronDownSmall from '@picsart/icons/IconChevronDownSmall';
import { useTranslator } from '@picsart/rc/services/localization';

import { TAnyObject } from 'types';
import useClickOutside from 'hooks/common/useClickOutside';
import { zoomValues } from 'configs/constants';
import ConditionalWrapper from 'components/common/ConditionalWrapper';
import useStyles from 'components/common/Dropdown/useStyles';

type HorizontalPositionType = 'left' | 'right' | 'center';
type VerticalPositionType = 'top' | 'bottom' | 'auto';
type DropdownSizeType = 'large' | 'medium' | 'small' | 'mini';

interface IDropdownProps {
  data: TAnyObject[];
  labelName: string;
  valueName: string;
  onChange: (value: TAnyObject) => void;
  selected: { [key: string]: any };
  horizontalPosition?: HorizontalPositionType;
  verticalPositionType?: VerticalPositionType;
  size?: DropdownSizeType;
  tooltip?: string;
  tooltipPlacement?: placementsList;
  [key: string]: any;
}

function Dropdown({
  data,
  labelName,
  valueName,
  onChange,
  selected,
  horizontalPosition = 'right',
  verticalPositionType = 'auto',
  size = 'medium',
  tooltip = '',
  tooltipPlacement = placementsList.bottom,
  ...restProps
}: IDropdownProps) {
  const classes = useStyles() as Record<string, string>;
  const translate = useTranslator();

  const [active, setActive] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((event: ChangeEvent<HTMLElement>) => {
    if (wrapperRef.current?.contains(event.target)) {
      return;
    }
    setActive(false);
  }, []);

  const optionsRef = useClickOutside(handleClickOutside);

  const optionsWrapperStyles = classNames(
    classes['pw-dropdown_content'],
    classes[`pw-dropdown_content_${horizontalPosition}`],
    classes[`pw-dropdown_content_${verticalPositionType}`],
  );

  return (
    <div
      {...restProps}
      ref={wrapperRef}
      className={classNames(classes['pw-dropdown'], classes[`pw-dropdown_${size}`])}
    >
      <ConditionalWrapper
        condition={!!tooltip}
        wrapper={children => <Tooltip content={tooltip} placement={tooltipPlacement}>{children as ReactElement}</Tooltip>}
      >
        <div
          onClick={() => setActive(state => !state)}
          className={classNames(classes['pw-dropdown_label'], { [classes.active]: active })}
        >
          <span>{selected[labelName]}</span>
          <IconChevronDownSmall className={classes.dropdownArrow} />
        </div>
      </ConditionalWrapper>
      { active
        && (
        <div
          className={optionsWrapperStyles}
        >
          <div className={classes['pw-dropdown_content_main']} ref={optionsRef}>
            {
              data.map(item => (
                <Fragment key={item.id || item[valueName]}>
                  {item.value === zoomValues.fit.value && <div className={classes['pw-dropdown_content_divider']} />}
                  <div
                    className={classes['pw-dropdown_content_item']}
                    onClick={() => {
                      onChange(item);
                      setActive(false);
                    }}
                  >
                    <span
                      className={classNames(
                        classes[`pw-dropdown_${size}`],
                        { [classes['pw-dropdown_content_item-selected']]: (item.id || item[valueName]) === (selected.id || selected[valueName]) },
                      )}
                    >
                      {translate(item[labelName])}
                    </span>
                  </div>
                </Fragment>
              ))
            }
          </div>
        </div>
        )}
    </div>
  );
}

export default memo(Dropdown);
